import {observer} from "mobx-react";

import * as Styled from "./MainMenuLink.styles";
import {useRootStore} from "stores";
import Icon from "components/common/Icon";
import type UIStore from "stores/UIStore";
import {PopoverContent, PopoverPortal, PopoverTrigger, Root} from "@radix-ui/react-popover";
import {MiniChangelog} from "./mini-changelog";
import {useMutation, useQuery} from "@tanstack/react-query";
import {Changelog} from "data/changelog";
import {subYears} from "date-fns";

const Now = new Date();

const WhatsNewMenuLink = () => {
  const {uiStore} = useRootStore() as unknown as {
    uiStore: UIStore;
  };

  const changelogs = useQuery(
    Changelog.queries.changelogs({timestamp_start: subYears(Now, 1).toISOString(), timestamp_end: Now.toISOString()}),
  );
  const newChangelogIDs = changelogs.data?.items.filter((item) => !item.seen).map((curr) => curr.changelog_id);

  const seenMutation = useMutation({
    mutationFn: (seen: string[]) => Changelog.Api.markSeenChangelogs(seen),
    onError: (err) => console.error(err),
  });
  const showNotification = newChangelogIDs && newChangelogIDs.length > 0 && uiStore.whatsNewNotification;

  // NOTE: only for the transition period of not having changelogs, else assume there's always changelogs present
  if (!changelogs.data?.items.length) return null;

  return (
    <Root>
      <PopoverTrigger
        asChild
        onClick={() => {
          if (!uiStore.whatsNewNotification || !newChangelogIDs) return;
          uiStore.removeWhatsNewNotification();
          seenMutation.mutate(newChangelogIDs);
        }}
      >
        <Styled.Highlight
          // @ts-expect-error no overload matches this call
          active={false}
          highlight={false}
          collapsed={uiStore.mainMenuCollapsed}
        >
          {/* @ts-expect-error no overload matches this call*/}
          <Styled.IconContainer collapsed={uiStore.mainMenuCollapsed}>
            <Icon
              library={Icon.Libraries.FontAwesome}
              name="star"
            />
            <div className="relative">
              {showNotification && uiStore.mainMenuCollapsed && (
                <div
                  style={{width: 15, height: 15, left: -9, bottom: -4}}
                  className="bg-content-main-menu-notification absolute bg-blue-500 rounded-full flex flex-row justify-center items-center"
                >
                  <div
                    style={{fontSize: "1.1rem", lineHeight: "1.5rem"}}
                    className="text-white font-semibold"
                  >
                    {newChangelogIDs.length > 4 ? "4+" : newChangelogIDs.length.toString()}
                  </div>
                </div>
              )}
            </div>
          </Styled.IconContainer>
          {!uiStore.mainMenuCollapsed && (
            <div className="flex flex-row items-center justify-between flex-1">
              <span>What&lsquo;s New</span>
              {showNotification && (
                <div
                  style={{width: 20, height: 15}}
                  className="bg-content-main-menu-notification rounded-full flex flex-row justify-center items-center"
                >
                  <div
                    style={{fontSize: "1.1rem", lineHeight: "1.5rem"}}
                    className="text-white font-semibold"
                  >
                    {newChangelogIDs.length > 4 ? "4+" : newChangelogIDs.length.toString()}
                  </div>
                </div>
              )}
            </div>
          )}
        </Styled.Highlight>
      </PopoverTrigger>
      <PopoverPortal>
        <PopoverContent
          style={{width: 185, zIndex: 50}}
          className="bg-content-main-menu-bg-alt relative rounded-md data-[state=open]:data-[side=bottom]:animate-slideUpAndFade data-[state=open]:data-[side=top]:animate-slideDownAndFade"
          sideOffset={5}
          side="top"
        >
          <MiniChangelog changelogs={changelogs.data.items.slice(0, 4)} />
        </PopoverContent>
      </PopoverPortal>
    </Root>
  );
};

export default observer(WhatsNewMenuLink);
